import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAppState } from "@/context/AppState";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import StationCard from "@/components/StationCard";
import WorldGlobe from "@/components/WorldGlobe";
import MoodGrid from "@/components/MoodGrid";
import GenresPage from "@/components/GenresPage";
import TopNav from "@/components/TopNav";
import { Radio, ArrowLeft, Loader2, Trash2, Shuffle } from "lucide-react";
import { findMood, findDecade } from "@/lib/moods";
import { toast } from "sonner";

export default function HomePage() {
  const { tab, view, setView, filters, setFilters, searchQuery } = useAppState();
  const { favorites, history, clearHistory, playStation } = usePlayer();
  const { t } = useI18n();

  const [countries, setCountries] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.countries()
      .then(setCountries)
      .catch((err) => console.warn("[voix] countries fetch failed:", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (view === "landing" || view === "favorites" || view === "history") { setStations([]); return; }
      setLoading(true);
      try {
        let data = [];
        if (view === "top") {
          data = await api.topStations(60);
        } else if (view === "filter" || view === "mood" || view === "decade") {
          const params = { limit: 120 };
          if (filters.country) params.country = filters.country;
          if (filters.tag) params.tag = filters.tag;
          if (filters.language) params.language = filters.language;
          data = await api.searchStations(params);
        } else if (view === "search" && searchQuery.trim()) {
          data = await api.searchStations({ name: searchQuery.trim(), limit: 80 });
        }
        if (!cancelled) setStations(data);
      } catch (err) {
        console.warn("[voix] station fetch failed:", err);
        if (!cancelled) setStations([]);
      }
      if (!cancelled) setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [view, filters, searchQuery]);

  const onSelectCountry = (name) => {
    setFilters({ ...filters, country: name });
    setView("filter");
  };

  const surpriseMe = async () => {
    try {
      const s = await api.randomStation({});
      playStation(s);
    } catch {
      toast.error(t("toast.no_random_global"));
    }
  };

  const displayed = useMemo(() => {
    if (view === "favorites") return favorites;
    if (view === "history") return history;
    return stations;
  }, [view, favorites, history, stations]);

  const title = useMemo(() => {
    if (view === "top") return { pre: t("view.top.pre"), main: t("view.top.main") };
    if (view === "favorites") return { pre: t("view.favorites.pre"), main: t("view.favorites.main") };
    if (view === "history") return { pre: t("view.history.pre"), main: t("view.history.main") };
    if (view === "search") return { pre: t("view.search.pre"), main: searchQuery ? `${t("view.search.results")} “${searchQuery}”` : t("view.search.type") };
    if (view === "mood") {
      const mood = findMood(filters.mood);
      return { pre: t("view.mood.pre"), main: mood ? t(`mood.${mood.key}`) : "" };
    }
    if (view === "decade") {
      const d = findDecade(filters.decade);
      return { pre: t("view.decade.pre"), main: d ? `${d.label} ${t("view.decade.suffix")}` : "" };
    }
    if (view === "filter") {
      const parts = [filters.country, filters.tag, filters.language].filter(Boolean);
      return { pre: t("view.filter.pre"), main: parts.join(" · ") || t("view.filter.pre") };
    }
    return null;
  }, [view, filters, searchQuery, t]);

  const isLanding = view === "landing";
  const isGridView = !isLanding;

  return (
    <div className="min-h-screen">
      <TopNav />

      {isLanding && tab === "moods" && <MoodGrid />}
      {isLanding && tab === "genres" && <GenresPage />}
      {isLanding && tab === "places" && (
        <div className="h-[calc(100vh-176px)] relative">
          <button
            data-testid="surprise-btn"
            onClick={surpriseMe}
            className="absolute top-4 right-4 z-20 flex items-center gap-2 border border-yellow-500 bg-yellow-500/10 text-yellow-300 px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest hover:bg-yellow-500/20 transition"
            title="Random station worldwide"
          >
            <Shuffle size={12} />
            <span>{t("landing.surprise")}</span>
          </button>
          <WorldGlobe countries={countries} onSelect={onSelectCountry} selectedCountry={filters.country} />
        </div>
      )}

      {isGridView && (
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <button
                data-testid="back-to-landing"
                onClick={() => setView("landing")}
                className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-400 transition mb-2"
              >
                <ArrowLeft size={12} />
                <span>{t("view.back")} {t(`nav.${tab}`)}</span>
              </button>
              {title && (
                <>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-cyan-400">{title.pre}</div>
                  <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">{title.main}</h1>
                </>
              )}
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                {displayed.length} {displayed.length === 1 ? t("view.stations") : t("view.stations_plural")}
              </div>
            </div>
            {view === "history" && history.length > 0 && (
              <button
                data-testid="clear-history"
                onClick={clearHistory}
                className="flex items-center gap-2 border border-[var(--voix-border)] px-3 py-2 text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:border-pink-500 hover:text-pink-400 transition"
              >
                <Trash2 size={12} />
                <span>Clear</span>
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="animate-spin text-cyan-400 mb-3" size={28} />
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">{t("view.tuning").toUpperCase()}</div>
            </div>
          ) : displayed.length === 0 ? (
            <EmptyState view={view} t={t} />
          ) : (
            <div data-testid="station-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
              {displayed.map((s) => <StationCard key={s.stationuuid} station={s} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ view, t }) {
  const keyMap = {
    favorites: "favorites", history: "history", search: "search",
    filter: "filter", mood: "mood", decade: "decade", top: "top",
  };
  const k = keyMap[view] || "filter";
  return (
    <div className="border border-dashed border-[var(--voix-border)] py-20 text-center">
      <Radio className="mx-auto text-slate-700 mb-3" size={36} strokeWidth={1.2} />
      <div className="font-heading font-bold text-xl">{t(`empty.${k}.t`)}</div>
      <div className="font-body text-sm text-slate-500 mt-1">{t(`empty.${k}.s`)}</div>
    </div>
  );
}
